import React, { useCallback, useRef, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase-client';
import { useAuth } from '../../auth/AuthContext';
import { CREATE_ACCOUNT_LINK_URL } from '../../utils/functionURLs';
import StripeRequiredDialog from './StripeRequiredDialog';
import { isStoredStripePaymentReady } from './stripeReadinessSnapshot';

const getPaidFeatureStripeReadiness = httpsCallable(functions, 'getPaidFeatureStripeReadiness');

export default function useStripePaymentGuard(teamId) {
  const { user } = useAuth();
  const pendingRef = useRef(null);
  const requestSequenceRef = useRef(0);
  const [dialog, setDialog] = useState({ open: false, state: '', featureLabel: '', canManageStripe: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const close = useCallback(() => {
    if (busy) return;
    requestSequenceRef.current += 1;
    pendingRef.current = null;
    setError('');
    setDialog((current) => ({ ...current, open: false }));
  }, [busy]);

  const requireStripePayment = useCallback(async ({ feature, featureLabel, returnTarget = feature, resumeEntity = '', onReady }) => {
    if (!teamId) return false;
    const requestSequence = requestSequenceRef.current + 1;
    requestSequenceRef.current = requestSequence;
    pendingRef.current = { feature, featureLabel, returnTarget, resumeEntity, onReady };
    setError('');
    setDialog({ open: true, state: 'checking', featureLabel, canManageStripe: false });
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setDialog({ open: true, state: 'unavailable', featureLabel, canManageStripe: false });
      setError('Connect to the internet to check the club’s Stripe setup.');
      return false;
    }
    try {
      // The server keeps these fields in sync whenever Stripe is connected,
      // refreshed, or a payment is attempted. Use that snapshot for the common
      // ready case so opening a paid feature does not wait for a Cloud Function
      // cold start and a Stripe API round trip.
      const setupSnap = await getDoc(doc(db, 'teams', teamId, 'stripe', 'setup')).catch(() => null);
      if (setupSnap?.exists() && isStoredStripePaymentReady(setupSnap.data())) {
        if (requestSequenceRef.current !== requestSequence) return false;
        pendingRef.current = null;
        setDialog((current) => ({ ...current, open: false }));
        onReady?.();
        return true;
      }

      // A missing or non-ready snapshot may simply mean the club has just
      // returned from Stripe onboarding. Refresh it live before showing the
      // setup/restriction dialog, and retain the server-side permission check.
      const result = await getPaidFeatureStripeReadiness({ teamId, feature });
      if (requestSequenceRef.current !== requestSequence) return false;
      const readiness = result.data || {};
      if (readiness.ready) {
        pendingRef.current = null;
        setDialog((current) => ({ ...current, open: false }));
        onReady?.();
        return true;
      }
      setDialog({
        open: true,
        state: readiness.state || 'restricted',
        featureLabel,
        canManageStripe: readiness.canManageStripe === true,
      });
      return false;
    } catch (err) {
      if (requestSequenceRef.current !== requestSequence) return false;
      setDialog({ open: true, state: 'unavailable', featureLabel, canManageStripe: false });
      setError(err?.message || 'Stripe readiness could not be checked. Try again.');
      return false;
    }
  }, [teamId]);

  const continueToStripe = useCallback(async () => {
    const pending = pendingRef.current;
    if (!pending || !user) return;
    setBusy(true);
    setError('');
    try {
      const token = await user.getIdToken();
      const response = await fetch(CREATE_ACCOUNT_LINK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ teamId, returnTarget: pending.returnTarget, resumeEntity: pending.resumeEntity || undefined }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Unable to open Stripe.');
      if (!payload?.url) throw new Error('Stripe setup link is not available.');
      window.location.assign(payload.url);
    } catch (err) {
      setError(err?.message || 'Unable to open Stripe.');
      setBusy(false);
    }
  }, [teamId, user]);

  const stripeGuardDialog = (
    <StripeRequiredDialog
      {...dialog}
      busy={busy}
      error={error}
      onContinue={continueToStripe}
      onClose={close}
    />
  );

  return { requireStripePayment, stripeGuardDialog };
}
